import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CreditCard, CheckCircle, Loader2, ShieldCheck, AlertTriangle, Lock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { api, paymentsAPI } from '../services/api';
import { useT } from '../services/i18n';

const STATE_MSG = {
  paid: 'paylink.paid', expired: 'paylink.expired', revoked: 'paylink.revoked', cancelled: 'paylink.cancelled',
};

const PaymentLinkPage = () => {
  const { token } = useParams();
  const { t } = useT();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/pay/${token}`).then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || t('paylink.invalid')));
  }, [token, t]);

  const pay = async () => {
    setBusy(true); setError('');
    try { const r = await paymentsAPI.payLink(token); window.location.assign(r.checkout_url); } catch (e) { const d = e.response?.data?.detail; setError(typeof d === 'string' ? d : t('paylink.failed')); setBusy(false); }
  };

  const eur = (v) => `${Number(v || 0).toFixed(2)} €`;

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-10" data-testid="payment-link-page">
        {!data && !error && <div className="flex justify-center py-20"><Loader2 className="animate-spin text-orange-500" /></div>}
        {error && !data && <div className="bg-white rounded-xl shadow p-8 text-center" data-testid="paylink-error"><AlertTriangle className="mx-auto text-red-500 mb-3" /><p className="text-gray-800 font-medium">{error}</p></div>}
        {data && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <section className="md:col-span-3 bg-white rounded-xl shadow-sm p-6">
              <p className="text-xs uppercase tracking-wide text-gray-500">{t('paylink.orderFor', { n: `${data.customer.first_name} ${data.customer.last_name}`.trim() })}</p>
              <h1 className="text-2xl font-bold text-gray-900 mb-4" data-testid="paylink-order-number">{t('checkout.orderNumber')} {data.order_number}</h1>
              {data.state !== 'open' && (
                <div className={`mb-4 rounded-lg p-4 text-sm flex gap-2 ${data.state === 'paid' ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-900'}`} data-testid={`paylink-state-${data.state}`}>
                  {data.state === 'paid' ? <CheckCircle size={18} /> : <AlertTriangle size={18} />} {t(STATE_MSG[data.state])}
                </div>
              )}
              <ul className="divide-y" data-testid="paylink-items">
                {data.items.map((i, idx) => (
                  <li key={idx} className="py-3 flex items-center gap-3">
                    {i.image && <img src={i.image} alt="" className="w-12 h-12 object-contain rounded border bg-white" />}
                    <div className="flex-1 min-w-0"><p className="text-xs text-gray-500">{i.brand}</p><p className="text-sm font-medium text-gray-900 truncate">{i.name}</p></div>
                    <span className="text-sm text-gray-600">× {i.quantity}</span>
                    <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{eur(i.price * i.quantity)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 border-t pt-3 text-sm space-y-1">
                <div className="flex justify-between text-gray-600"><span>{t('common.subtotal')}</span><span>{eur(data.subtotal)}</span></div>
                <div className="flex justify-between text-gray-600"><span>{t('common.shipping')}</span><span>{data.shipping ? eur(data.shipping) : t('common.free')}</span></div>
                {data.wallet_used > 0 && <div className="flex justify-between text-green-700"><span>{t('checkout.walletUsed')}</span><span>−{eur(data.wallet_used)}</span></div>}
                <div className="flex justify-between text-lg font-bold text-gray-900 pt-2"><span>{t('paylink.toPay')}</span><span data-testid="paylink-amount" className="text-orange-600">{eur(data.paid_amount)}</span></div>
              </div>
            </section>
            <aside className="md:col-span-2 bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2 mb-4"><CreditCard size={18} className="text-orange-500" /> {t('paylink.cardTitle')}</h2>
              {data.state === 'open' ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600" data-testid="paylink-sumup-info">{t('paylink.sumupInfo')}</p>
                  <div className="flex gap-2 flex-wrap">{['Visa', 'Mastercard', 'Apple Pay', 'Google Pay'].map((i) => <span key={i} className="px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-600">{i}</span>)}</div>
                  {error && <p className="text-sm text-red-600" data-testid="paylink-form-error">{error}</p>}
                  <Button data-testid="paylink-pay-button" onClick={pay} disabled={busy} className="w-full bg-orange-500 hover:bg-orange-600 h-11">
                    {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Lock size={16} className="mr-2" />} {busy ? t('checkout.redirecting') : t('paylink.payNow', { a: eur(data.paid_amount) })}
                  </Button>
                  <p className="text-[11px] text-gray-500 flex items-center gap-1"><ShieldCheck size={12} className="text-green-600" /> {t('paylink.secure')}</p>
                </div>
              ) : (
                <Link to="/mes-commandes" className="text-sm text-orange-600 font-medium hover:underline">{t('checkout.viewOrders')}</Link>
              )}
            </aside>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default PaymentLinkPage;
